import { createInstance, type Resource } from 'i18next';

import { englishResources } from './resources';

export type UiLanguage = 'en' | 'id';
export const isUiLanguage = (value: unknown): value is UiLanguage =>
    value === 'en' || value === 'id';

export const languageStorageKey = (userId: string, organizationId: string) =>
    `atomix:hospital:language:${encodeURIComponent(userId)}:${encodeURIComponent(organizationId)}`;

export const readLanguagePreference = (
    storage: Pick<Storage, 'getItem'>,
    key: string,
): UiLanguage => {
    try {
        const stored = storage.getItem(key);
        return isUiLanguage(stored) ? stored : 'id';
    } catch {
        return 'id';
    }
};

export const createUiI18n = (
    language: UiLanguage,
    indonesian?: Resource[string],
) => {
    const instance = createInstance();
    void instance.init({
        lng: language,
        supportedLngs: ['en', 'id'],
        fallbackLng: 'en',
        defaultNS: 'common',
        ns: ['common', 'hospital', 'bpjs', 'bpjsFields'],
        resources: {
            en: englishResources,
            ...(indonesian ? { id: indonesian } : {}),
        },
        initAsync: false,
        returnNull: false,
        returnEmptyString: false,
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
        // Only catalog identifiers are logged. Never log interpolation values.
        ...(import.meta.env.DEV
            ? {
                  saveMissing: true,
                  missingKeyHandler: (
                      _languages: readonly string[],
                      namespace: string,
                      key: string,
                  ) => {
                      console.warn('Missing UI translation', {
                          namespace,
                          key,
                      });
                  },
              }
            : {}),
    });
    return instance;
};
